import { useQuery } from "@apollo/client";
import { Alert, Autocomplete, Button, TextField } from "@mui/material";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { gql } from "generated-graphql";
import { GeneratorStatusExceedanceCorrectableManifestsQuery } from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { useState } from "react";

import { ExceedanceKey } from "./exceedanceKeys";

const MANIFESTS_QUERY = gql(`
  query GeneratorStatusExceedanceCorrectableManifests(
    $facilityId: ID!, $source: String!, $periodStart: Date!, $search: String
  ) {
    generatorStatusExceedanceCorrectableManifests(
      facilityId: $facilityId, source: $source, periodStart: $periodStart, search: $search
    ) { id manifestTrackingNumber shippedDate }
  }
`);

export const CORRECTABLE_WASTE_LINES_QUERY = gql(`
  query GeneratorStatusExceedanceCorrectableWasteLines(
    $facilityId: ID!, $source: String!, $periodStart: Date!, $processedManifestId: ID!
  ) {
    generatorStatusExceedanceCorrectableWasteLines(
      facilityId: $facilityId, source: $source, periodStart: $periodStart,
      processedManifestId: $processedManifestId
    ) { id wasteLineNumber documentAmount }
  }
`);

export type CorrectionTarget = { processedManifestId: string; wasteLineUnifiedId: string | null };

export type ManifestOption =
  GeneratorStatusExceedanceCorrectableManifestsQuery["generatorStatusExceedanceCorrectableManifests"][number];

function manifestOptionLabel(manifest: ManifestOption): string {
  return `${manifest.manifestTrackingNumber} · shipped ${getUtcDisplayDate(manifest.shippedDate, "med")}`;
}

export function CorrectionManifestPicker({
  exceedanceKey,
  manifest,
  onChange,
}: {
  exceedanceKey: ExceedanceKey;
  manifest: ManifestOption | null;
  onChange: (manifest: ManifestOption | null) => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const key = {
    facilityId: exceedanceKey.facilityId,
    source: exceedanceKey.source,
    periodStart: exceedanceKey.periodStart,
  };
  const { data, loading, error, refetch } = useQuery(MANIFESTS_QUERY, {
    variables: { ...key, search: debouncedSearch },
    fetchPolicy: "cache-and-network",
  });
  const manifests = data?.generatorStatusExceedanceCorrectableManifests ?? [];
  return (
    <>
      {error && (
        <Alert severity="error" action={<Button onClick={() => void refetch()}>Retry</Button>}>
          Could not load manifests.
        </Alert>
      )}
      <Autocomplete
        options={manifests}
        value={manifest}
        loading={loading}
        // The query searches server-side; the newest 50 show until staff type.
        filterOptions={(options) => options}
        getOptionLabel={manifestOptionLabel}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText="No matching manifests shipped in this period."
        onInputChange={(_event, value, reason) => {
          if (reason !== "input" && reason !== "clear") return;
          setSearch(value);
        }}
        onChange={(_event, value) => {
          onChange(value);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Manifest"
            size="small"
            required
            helperText="Type a tracking number to find an older manifest."
          />
        )}
      />
    </>
  );
}
